<template>
  <div>
    <template v-if="!pageDetector.isTopPage">
      <h3 class="commonHeadingGrayBorder spHidden"><icon far="fa-calendar-alt" /> カレンダーから街コン・婚活を探す</h3>
      <month-calendar class="marginB4 spHidden" />
    </template>
    <side-link-area :content="contents.monthly_event" />
    <side-link-area v-if="pageDetector.isOnlineSpecialPage" :content="onlineFeaturesLinks" :inline="false" />
    <side-link-area :content="contents.interest" />
    <side-link-area :content="contents.subarea" />
    <template v-if="!pageDetector.isTopPage && !pageDetector.isFixedEventsPage">
      <h3 class="commonHeadingGrayBorder"><icon fas="fa-search" /> 開催地・開催日で街コン・婚活を探す</h3>
      <search-by-area-and-date />
    </template>
    <side-link-area :content="contents.other_category" :inline="false" />
    <!-- Public Relations -->
    <public-relations v-if="!isAdvertiseFeaturePage && !pageDetector.isEventDetailPage" :custom-for-side-content="true" />
    <div>
      <h3 class="commonHeadingGrayBorder"><icon fas="fa-search" /> キーワードで街コン・婚活を探す</h3>
      <form class="searchBox">
        <div class="searchBox-container">
          <input v-model="keyword" class="searchBox-input" placeholder="キーワードで探す" @focus="showSuggestion" @blur="hideSuggestion" />
          <div v-show="displaySuggestion" class="searchBox-suggestion">
            <div
              v-for="keywordSuggestion in keywordSuggestions"
              :key="keywordSuggestion.id"
              class="searchBox-suggestion-item"
              @mousedown="setKeyword(keywordSuggestion.keyword)"
            >
              {{ keywordSuggestion.keyword }}
            </div>
          </div>
        </div>
        <button class="searchBox-button commonButton btn-secondary-40" @click.prevent="toSearchResult">検索</button>
      </form>
    </div>
    <side-link-area :content="popularKeyword" :show-empty-link-as-text="false" />
    <side-link-area :content="contents.trend_event" :icon="{ far: 'fa-comment' }" />
    <side-link-area :content="contents.ranking" :icon="{ fas: 'fa-trophy' }" />
    <side-link-area :content="contents.promoter" :show-empty-link-as-text="false" :inline="false" />
    <google-ads v-if="pageDetector.isAreaWithGoogleAdsPage" :number-ads="2" />
  </div>
</template>
<script>
const MonthCalendar = defineAsyncComponent(() => import('@/components/calendar/MonthCalendar.vue'))
const SideLinkArea = defineAsyncComponent(() => import('@/components/side/SideLinkArea.vue'))
import SideContentMixin from '@/utils/mixins/SideContent'
import { findPrefectureBySlug } from '@/models/places'
import { AREAS_MARRIAGE_SITE } from '@/utils/constants/marriageSite'
const GoogleAds = defineAsyncComponent(() => import('@/components/GoogleAds.vue'))
import PageDetector from '@/utils/PageDetector'
import TrackingUserMixin from '@/utils/mixins/TrackingUserMixin'
import SearchKeywordsMixin from '@/utils/mixins/SearchKeywordsMixin'
const SearchByAreaAndDate = defineAsyncComponent(() => import('@/components/side/SearchByAreaAndDate.vue'))
export default {
  components: { MonthCalendar, SideLinkArea, SearchByAreaAndDate, GoogleAds },
  mixins: [SideContentMixin, TrackingUserMixin, SearchKeywordsMixin],
  data() {
    return {
      popularKeywords: ['自衛隊', '年上女性', '高身長', '医療', '高収入', 'マッチョ']
    }
  },
  computed: {
    pageDetector() {
      return new PageDetector(this.$route)
    },
    popularKeyword() {
      const list = this.popularKeywords.map(keyword => ({ name: keyword, link: `/?s=${keyword}` }))
      const area = this.findAreaMarriageBySlug(this.$route.params.area)
      const prefecture = findPrefectureBySlug(this.$route.params.prefecture)
      if (this.pageDetector.isPrefectureKonkatsuPage && prefecture && prefecture.marriage_site_url && !area) {
        list.push({ name: `${prefecture.name}の結婚相談所`, link: prefecture.marriage_site_url })
      }
      if (this.pageDetector.isAreasCategoriesKonkatsuPage && area && area.marriage_site_url) {
        list.push({ name: `${area.name}の結婚相談所`, link: area.marriage_site_url })
      }
      return {
        title: '他の人はこちらも検索',
        list: list
      }
    },
    eventType() {
      const searchEventTypes = this.apiSearchParams['filter[event_type]'] ? this.apiSearchParams['filter[event_type]'].split(',') : []
      return searchEventTypes[0] || this.options.eventType
    },
    searchConditions() {
      return this.$store.$state.search_page.search_conditions
    },
    isAdvertiseFeaturePage() {
      return this.searchConditions && this.searchConditions.feature_tag && this.searchConditions.feature_tag.advertisement
    },
    onlineFeaturesLinks() {
      return {
        title: 'オンラインイベントを探す',
        list: [
          { name: 'オンライン街コン', link: '/features/online_events/' },
          { name: 'オンライン婚活・お見合い', link: '/features/online_konkatsu/' },
          { name: 'オンライン友活', link: '/features/online_tomokatsu/' }
        ]
      }
    }
  },
  methods: {
    findAreaMarriageBySlug(slug) {
      return AREAS_MARRIAGE_SITE.find(v => v.slug === slug)
    },
    toSearchResult() {
      this.$store.dispatch('createOrUpdateKeywordSuggestion', { keyword: this.keyword })
      this.$router.push({ path: '/', query: { s: this.keyword } })
      if (this.keyword) {
        this.sendOpixSearchByKeyword(this.keyword)
      }
    },
    setKeyword(keyword) {
      this.keyword = keyword
      this.toSearchResult()
    }
  }
}
</script>

<style scoped lang="scss">
.searchBox {
  @include flex;
  margin: 1rem 0 1.5rem;
  &-container {
    position: relative;
    margin-right: 1rem;
    flex: 1;
  }
  &-input {
    width: 100%;
    padding: 10px;
    margin-right: 1rem;
    border: 1px solid $gray-c6;
    border-radius: 3px;
    box-shadow: inset 0 1px 2px $black-01;
    &:focus {
      border-color: $gray-55;
      box-shadow: 0 0 5px $gray-c6;
      outline: none;
    }
  }
  &-button {
    min-width: 5em;
    padding: 10px;
  }
  &-suggestion {
    position: absolute;
    border: 1px solid $gray-f1;
    border-bottom: none;
    border-top: none;
    z-index: 99;
    top: 100%;
    left: 0;
    right: 0;
    &-item {
      padding: 10px;
      cursor: pointer;
      background-color: $white;
      border-bottom: 1px solid $gray-f1;
      &:hover {
        background-color: $gray-f1;
      }
    }
  }
}
</style>
